import React, { SyntheticEvent, Dispatch } from 'react'
import { useHistory } from 'react-router-dom'
import { isEmpty } from 'lodash'

import { BulletLabel, Skeleton } from 'components'
import { TBody, Row, ActionEventTable } from 'components/Table'
import { IconBoxProduct } from 'components/Icons'
import { IBranchSingleProps } from '../model'

import type { IColumn } from 'types'
import { getPhoneFormatter } from 'helpers/common'
import { getFullAddress } from 'helpers/address'

interface Props {
  columns: IColumn[]
  branches: IBranchSingleProps[]
  loading: boolean
  isError: boolean
  showMenu: boolean
  onClickRow: (id: string) => (e: SyntheticEvent) => void
  setIsClickBoxProduct: Dispatch<React.SetStateAction<boolean>>
}

const SkeletonListOdd: Record<string, React.ReactNode> = {
  no: <Skeleton.whiteSkeleton width='92px' />,
  name: <Skeleton.whiteSkeleton width='89px' />,
  companyName: <Skeleton.whiteSkeleton width='157px' />,
  phone: <Skeleton.whiteSkeleton />,
  address: <Skeleton.whiteSkeleton width='240px' />,
}

const SkeletonListEven: Record<string, React.ReactNode> = {
  no: <Skeleton.whiteSkeleton width='131px' />,
  name: <Skeleton.whiteSkeleton width='146px' />,
  companyName: <Skeleton.whiteSkeleton width='245px' />,
  phone: <Skeleton.whiteSkeleton />,
  address: <Skeleton.whiteSkeleton width='308px' />,
}

export const BranchList: React.FC<Props> = (props: Props) => {
  const history = useHistory()

  const {
    columns,
    branches,
    loading,
    isError,
    onClickRow,
    setIsClickBoxProduct,
  } = props

  const list: Record<string, React.ReactNode>[] = branches.map(
    (item, index) => {
      let address = ''

      if (item.addresses[0]) {
        address = getFullAddress('th', item.addresses[0], {
          hasCountry: false,
          separator: ', ',
        })
      }

      return {
        no: item.no,
        code: item.code,
        name: item.name,
        companyName: item.companyName,
        phone: getPhoneFormatter(item.phone) || '-',
        address,
        isActive: <BulletLabel isActive={Boolean(item.isActive)} />,
        branchProducts: (
          <IconBoxProduct
            onClick={() =>
              history.push(`/branch/${branches[index].id}/product`)
            }
            onMouseOver={() => setIsClickBoxProduct(true)}
            onMouseLeave={() => setIsClickBoxProduct(false)}
          />
        ),
      }
    },
  )

  if (loading) {
    return (
      <Skeleton.SkeletonList
        columns={columns}
        rowOdd={SkeletonListOdd}
        rowEven={SkeletonListEven}
      />
    )
  }

  if (isEmpty(branches) || isError) {
    return <ActionEventTable colSpan={columns.length} isNotFound />
  }

  return (
    <TBody>
      {list.map((data, index) => (
        <Row
          data-testid={`branch-row-${branches[index].id}`}
          key={index}
          columns={columns}
          data={data}
          onClick={onClickRow(branches[index].id as string)}
          position='left'
        />
      ))}
    </TBody>
  )
}

export default BranchList
